import { useCallback } from "react";

import Hls from "hls.js";

import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { UnauthorizedVideoRecording, VideoRecording } from "@app/common/types";

export type RecordingStatus =
  | "unsupported_browser"
  | "no_video"
  | "unauthorized"
  | "available"
  | "unknown";

interface UseRecordingStatus {
  status: RecordingStatus;
  message?: string;
}

interface RecordingInfo {
  recordingInfo?: VideoRecording | UnauthorizedVideoRecording;
  isLoading: boolean;
}

const getRecordingStatus = (
  info: RecordingInfo = { isLoading: false },
  permissions: {
    subscriptionSupportsRecording: boolean;
    canAccessPlayback: boolean;
  }
): UseRecordingStatus => {
  if (!Hls.isSupported()) {
    return {
      status: "unsupported_browser",
      message: "This browser does not support video playback",
    };
  }

  // canAccessPlayback could also be false if the user is not on the Enterprise plan,
  // but this specific video could be sponsored by someone on the Enterprise plan, so
  // we can't use that access alone to  determine if the user has access to this specific
  // recording. However, if the user cannot access playback but their subscription allows
  // for them to, that can only mean that they're in a workspace that has disabled playback
  const playbackDisabledForWorkspace =
    permissions.subscriptionSupportsRecording && !permissions.canAccessPlayback;

  if (playbackDisabledForWorkspace) {
    return {
      status: "unauthorized",
      message: "Audio and video playback is disabled for your Workspace",
    };
  }

  if (info.isLoading) {
    return { status: "unknown" };
  }

  if (info.recordingInfo == null) {
    return { status: "no_video" };
  }

  if (info.recordingInfo.status === "unauthorized") {
    return {
      status: "unauthorized",
      message: "You do not have access to this recording",
    };
  }

  return { status: "available" };
};

export const useGetRecordingStatus = () => {
  const { canAccessPlayback } = useFeatureAccess();
  const { subscription } = useBillingContext();

  const getRecordingStatusCallback = useCallback(
    (info?: RecordingInfo) =>
      getRecordingStatus(info, {
        subscriptionSupportsRecording: subscription.settings.storeRecording,
        canAccessPlayback,
      }),
    [subscription.settings.storeRecording, canAccessPlayback]
  );

  return getRecordingStatusCallback;
};

const useRecordingStatus = (info?: RecordingInfo): UseRecordingStatus => {
  const { canAccessPlayback } = useFeatureAccess();
  const { subscription } = useBillingContext();

  return getRecordingStatus(info, {
    subscriptionSupportsRecording: subscription.settings.storeRecording,
    canAccessPlayback,
  });
};

export default useRecordingStatus;
